import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router";
import { Segmented, Select, Skeleton, Empty, Tooltip, Tag } from "antd";
import {
  BarChartOutlined,
  HeatMapOutlined,
  TeamOutlined,
  UserOutlined,
  WarningOutlined,
  BulbOutlined,
  RightOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  RiseOutlined,
  FileZipOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import { PriorityBreakdown, RecoBucket } from "components/Dashboard/CompanyAnalyticsPanel";
import { issuePath } from "helpers/issueLinks";
import { useRememberRouteProject } from "helpers/projectSelection";
import {
  getProjectContributions,
  getProjectOverview,
  getProjectRecommendations,
} from "api/analytics/projectAnalyticsApi";

const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", sub: "#AEAEAE",
  line: "#EFEFEF", line2: "#E2E2E2", bg: "#FAFAFA", wash: "#E9F1F5",
  wash2: "#F3F7F9", navy: "#455D6B", navyD: "#3A5765", navyX: "#23343E",
  orange: "#FE6919", added: "#4FC92A", addedBg: "#EEFBE9", added2: "#317A1B",
  removed: "#FB8080", removedBg: "#FFF0F0", removed2: "#C53030",
  amber: "#E0A43A", ambBg: "#FDF4E1",
};

function Card({ title, subtitle, children, style, extra }) {
  return (
    <div style={{
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
      padding: 16, ...style,
    }}>
      {(title || extra) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: subtitle ? 2 : 12 }}>
          {title && <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{title}</div>}
          <span style={{ flex: 1 }} />
          {extra}
        </div>
      )}
      {subtitle && <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 12 }}>{subtitle}</div>}
      {children}
    </div>
  );
}

function Stat({ label, value, color = T.ink, icon }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em", marginBottom: 4 }}>
        {label.toUpperCase()}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color, display: "flex", alignItems: "center", gap: 8 }}>
        {icon}{value}
      </div>
    </div>
  );
}

function Pill({ kind = "neutral", children, size = "md" }) {
  const pad = size === "sm" ? "2px 7px" : "3px 9px";
  const fs = size === "sm" ? 9.5 : 10.5;
  const map = {
    neutral: { bg: "#F3F4F5", bd: "#E8EAEC", fg: "#526470" },
    navy: { bg: T.wash, bd: "#D4E3EC", fg: T.navyD },
    added: { bg: T.addedBg, bd: "#CDEEC1", fg: T.added2 },
    removed: { bg: T.removedBg, bd: "#FAD6D6", fg: T.removed2 },
    orange: { bg: "#FFF2E9", bd: "#FFD9BF", fg: "#B84B0A" },
    amber: { bg: T.ambBg, bd: "#F1DFB1", fg: "#8A6112" },
  };
  const s = map[kind] || map.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: s.bg, border: `1px solid ${s.bd}`, color: s.fg,
      padding: pad, borderRadius: 999,
      fontSize: fs, fontWeight: 600, lineHeight: 1, letterSpacing: ".02em",
    }}>{children}</span>
  );
}

// ─── Contributor bars ─────────────────────────────────────────
function BarsRow({ days, daily, maxDay, height = 50 }) {
  if (!days?.length) return null;
  const peak = Math.max(1, maxDay);
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(${days.length}, 1fr)`,
      gap: 1, height, alignItems: "end",
    }}>
      {days.map(d => {
        const count = daily[d] || 0;
        const h = count ? Math.max(3, (count / peak) * height) : 2;
        return (
          <Tooltip key={d} title={`${moment(d).format("MMM D")} — ${count} event${count === 1 ? "" : "s"}`}>
            <div style={{
              height: h,
              background: count ? T.navy : T.line2,
              opacity: count ? Math.max(0.3, count / peak) : 1,
              borderRadius: 1,
            }} />
          </Tooltip>
        );
      })}
    </div>
  );
}

// ─── Contributor heatmap (week-grid) ──────────────────────────
function HeatmapGrid({ days, daily, maxDay }) {
  if (!days?.length) return null;
  const peak = Math.max(1, maxDay);
  // Build weeks: chunks of 7 from oldest → newest
  const weeks = [];
  let currentWeek = new Array(7).fill(null);
  days.forEach(d => {
    const dow = moment(d).day(); // 0=Sun
    if (dow === 0 && currentWeek.some(Boolean)) {
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
    }
    currentWeek[dow] = d;
  });
  if (currentWeek.some(Boolean)) weeks.push(currentWeek);

  const colorFor = (count) => {
    if (!count) return "#F3F4F5";
    const t = count / peak;
    if (t < 0.25) return "#D6EAD4";
    if (t < 0.5) return "#9ED29A";
    if (t < 0.75) return "#63B65C";
    return T.navy;
  };

  return (
    <div style={{ display: "flex", gap: 2 }}>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {week.map((d, di) => {
            if (!d) return <div key={di} style={{ width: 11, height: 11 }} />;
            const count = daily[d] || 0;
            return (
              <Tooltip key={di} title={`${moment(d).format("ddd MMM D")} — ${count} event${count === 1 ? "" : "s"}`}>
                <div style={{
                  width: 11, height: 11, borderRadius: 2,
                  background: colorFor(count),
                  border: `1px solid rgba(0,0,0,0.04)`,
                }} />
              </Tooltip>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Status/Priority mini visual ──────────────────────────────
function StatusBreakdown({ counts }) {
  const items = [
    { key: "open", label: "Open", color: "#3B82F6", icon: <ClockCircleOutlined /> },
    { key: "pending", label: "Pending", color: "#F59E0B", icon: <ExclamationCircleOutlined /> },
    { key: "resolved", label: "Resolved", color: "#10B981", icon: <CheckCircleOutlined /> },
    { key: "abandoned", label: "Abandoned", color: "#6B7280", icon: <StopOutlined /> },
  ];
  const total = Object.values(counts || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{
        display: "flex", height: 10, borderRadius: 999, overflow: "hidden",
        background: "#F3F4F5",
      }}>
        {items.map(it => {
          const pct = ((counts?.[it.key] || 0) / total) * 100;
          return <div key={it.key} style={{ width: `${pct}%`, background: it.color }} />;
        })}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {items.map(it => (
          <div key={it.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: it.color, flexShrink: 0,
            }} />
            <span style={{ color: T.text }}>{it.label}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontWeight: 600, color: T.ink }}>{counts?.[it.key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ─── Main page ────────────────────────────────────────────────
export default function ProjectAnalyticsPage() {
  const { projectId } = useParams();
  useRememberRouteProject(projectId);
  const history = useHistory();

  const [overview, setOverview] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [recos, setRecos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [days, setDays] = useState(90);
  const [viewMode, setViewMode] = useState("individuals"); // 'team' | 'individuals'
  const [chartStyle, setChartStyle] = useState("bars"); // 'bars' | 'heatmap'
  const [selectedUserIds, setSelectedUserIds] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    Promise.all([
      getProjectOverview(projectId),
      getProjectContributions(projectId, days),
      getProjectRecommendations(projectId),
    ])
      .then(([ov, co, rc]) => {
        if (cancelled) return;
        setOverview(ov); setContrib(co); setRecos(rc);
      })
      .catch(e => !cancelled && setErr(e.message || "Failed to load"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [projectId, days]);

  const contributors = contrib?.contributors || [];
  const dayList = contrib?.days || [];

  const filteredContributors = useMemo(() => {
    if (!selectedUserIds) return contributors;
    return contributors.filter(c => selectedUserIds.includes(String(c.user._id)));
  }, [contributors, selectedUserIds]);

  const teamAggregate = useMemo(() => {
    if (!dayList.length) return null;
    const daily = {};
    dayList.forEach(d => { daily[d] = 0; });
    let total = 0, maxDay = 0, activeDays = 0;
    filteredContributors.forEach(c => {
      dayList.forEach(d => {
        const v = c.daily[d] || 0;
        daily[d] += v;
        total += v;
      });
    });
    dayList.forEach(d => {
      if (daily[d] > maxDay) maxDay = daily[d];
      if (daily[d] > 0) activeDays += 1;
    });
    return { daily, total, maxDay, activeDays };
  }, [dayList, filteredContributors]);

  if (loading) {
    return (
      <div style={{ padding: "32px 40px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (err) {
    return (
      <div style={{ padding: "32px 40px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
        <div style={{
          padding: 16, background: T.removedBg, border: "1px solid #FAD6D6",
          borderRadius: 6, color: T.removed2,
        }}>{err}</div>
      </div>
    );
  }

  const reco = recos?.recommendations || { urgent: [], recommended: [], next: [] };
  const hasAnyReco = reco.urgent.length + reco.recommended.length + reco.next.length > 0;

  return (
    <div style={{ padding: "24px 32px", background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.ink, letterSpacing: "-.01em" }}>
          Project Analytics
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          {overview?.title} · {overview?.memberCount || 0} member{overview?.memberCount === 1 ? "" : "s"} · {overview?.activityLast30Days || 0} events in last 30 days
        </div>
      </div>

      {/* KPI row */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20,
      }}>
        <Card style={{ padding: 18 }}>
          <Stat label="Total issues" value={overview?.issues?.total || 0}
                icon={<FileZipOutlined style={{ color: T.navy, fontSize: 18 }} />} />
        </Card>
        <Card style={{ padding: 18 }}>
          <Stat label="Open + Pending" color="#B45309"
                value={(overview?.issues?.byStatus?.open || 0) + (overview?.issues?.byStatus?.pending || 0)} />
        </Card>
        <Card style={{ padding: 18 }}>
          <Stat label="Exports (30d)" color={T.added2} value={overview?.exports?.last30Days || 0} />
        </Card>
        <Card style={{ padding: 18 }}>
          <Stat label="Total exports" value={overview?.exports?.total || 0} />
        </Card>
      </div>

      {/* Issues + Recommendations side by side */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20,
      }}>
        <Card title="Issues by status">
          <div style={{ marginBottom: 14 }}>
            <StatusBreakdown counts={overview?.issues?.byStatus} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginBottom: 6 }}>
            BY PRIORITY
          </div>
          <PriorityBreakdown counts={overview?.issues?.byPriority} issues={overview?.issues?.byPriorityIssues} />
        </Card>

        <Card
          title="Recommendations"
          extra={<Pill kind="neutral" size="sm">Rule-based · review required</Pill>}
        >
          <RecoBucket
            title="Urgent" icon={<WarningOutlined />}
            color={T.removed2} bg={T.removedBg}
            items={reco.urgent}
          />
          <RecoBucket
            title="Recommended" icon={<BulbOutlined />}
            color={"#8A6112"} bg={T.ambBg}
            items={reco.recommended}
          />
          <RecoBucket
            title="Next action" icon={<RightOutlined />}
            color={T.navyD} bg={T.wash}
            items={reco.next}
          />
          {!hasAnyReco && (
            <Empty description="No action items — project looks healthy" />
          )}
          <div style={{
            marginTop: 8, fontSize: 10.5, color: T.muted, fontStyle: "italic",
          }}>
            {recos?.disclaimer}
          </div>
        </Card>
      </div>

      {/* Contributor activity */}
      <Card
        title={<>
          <span>Contributor activity</span>
          <span style={{ fontSize: 11, color: T.muted, marginLeft: 8, fontWeight: 400 }}>
            events per day · {contributors.length} contributor{contributors.length === 1 ? "" : "s"}
          </span>
        </>}
        extra={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Select
              value={days} onChange={setDays} size="small" style={{ width: 110 }}
              options={[
                { value: 30, label: "30 days" },
                { value: 60, label: "60 days" },
                { value: 90, label: "90 days" },
                { value: 180, label: "180 days" },
              ]}
            />
            <Segmented
              size="small" value={viewMode} onChange={setViewMode}
              options={[
                { value: "team", label: "Team", icon: <TeamOutlined /> },
                { value: "individuals", label: "Individuals", icon: <UserOutlined /> },
              ]}
            />
            <Segmented
              size="small" value={chartStyle} onChange={setChartStyle}
              options={[
                { value: "bars", icon: <BarChartOutlined /> },
                { value: "heatmap", icon: <HeatMapOutlined /> },
              ]}
            />
          </div>
        }
        style={{ marginBottom: 20 }}
      >
        {contributors.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <Select
              mode="multiple"
              placeholder="Filter contributors"
              style={{ width: "100%" }}
              size="small"
              value={selectedUserIds || contributors.map(c => String(c.user._id))}
              onChange={(v) => setSelectedUserIds(v.length === contributors.length ? null : v)}
              options={contributors.map(c => ({
                value: String(c.user._id),
                label: `${c.user.name} (${c.total})`,
              }))}
            />
          </div>
        )}

        {contributors.length === 0 ? (
          <Empty description="No contributor activity in range" />
        ) : viewMode === "team" ? (
          <div style={{ padding: "4px 0" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
              Team aggregate · {teamAggregate?.total || 0} events
              <span style={{ fontSize: 11, fontWeight: 400, color: T.muted, marginLeft: 8 }}>
                active {teamAggregate?.activeDays || 0}/{dayList.length} days · peak {teamAggregate?.maxDay || 0}/day
              </span>
            </div>
            {chartStyle === "bars" ? (
              <BarsRow days={dayList} daily={teamAggregate?.daily || {}} maxDay={teamAggregate?.maxDay || 0} height={72} />
            ) : (
              <HeatmapGrid days={dayList} daily={teamAggregate?.daily || {}} maxDay={teamAggregate?.maxDay || 0} />
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {filteredContributors.map(c => (
              <div key={c.user._id} style={{ padding: "8px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{
                    width: 22, height: 22, borderRadius: "50%", background: T.navy,
                    color: "#fff", fontSize: 10, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>{c.user.name.charAt(0).toUpperCase()}</div>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{c.user.name}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: T.muted }}>
                    {c.total} events · {c.activeDays} active days
                  </span>
                </div>
                {chartStyle === "bars" ? (
                  <BarsRow days={dayList} daily={c.daily} maxDay={c.maxDay} height={42} />
                ) : (
                  <HeatmapGrid days={dayList} daily={c.daily} maxDay={c.maxDay} />
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Recent activity */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12,
      }}>
        <Card title="Latest exports">
          {(overview?.latestExports || []).length === 0 ? (
            <Empty description="No exports yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {overview.latestExports.map(e => (
                <div key={e.id} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", background: T.bg, borderRadius: 6,
                }}>
                  <FileZipOutlined style={{ color: T.navy }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.title || "Export"}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {moment(e.date).fromNow()}
                      {e.author ? ` · ${e.author.name}` : ""}
                      {e.milestone ? ` · ${e.milestone}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Recent issues">
          {(overview?.recentIssues || []).length === 0 ? (
            <Empty description="No issues yet" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {overview.recentIssues.map(m => (
                <div key={m.id}
                  onClick={() => history.push(issuePath(m))}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px", background: T.bg, borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 500, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.label && <span style={{ fontFamily: "monospace", fontSize: 11, color: T.muted, marginRight: 6 }}>{m.label}</span>}
                      {m.title}
                    </div>
                    <div style={{ fontSize: 11, color: T.muted }}>
                      {moment(m.updatedAt).fromNow()} · {m.author?.name || "—"}
                    </div>
                  </div>
                  <Tag color={
                    m.status === "resolved" ? "green" :
                    m.status === "pending" ? "orange" :
                    m.status === "abandoned" ? "default" : "blue"
                  } style={{ margin: 0, fontSize: 10 }}>
                    {(m.status || "open").toUpperCase()}
                  </Tag>
                  <Link to={issuePath(m)} onClick={(e) => e.stopPropagation()}
                        style={{ fontSize: 10.5, fontWeight: 600, color: T.navyD, border: "1px solid #D4E3EC", background: T.wash, borderRadius: 4, padding: "2px 8px", whiteSpace: "nowrap" }}>
                    View issue
                  </Link>
                  {m.priority === "urgent" && <Pill kind="removed" size="sm"><RiseOutlined /> URGENT</Pill>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
